import React, { useEffect, useState } from 'react';
import { api } from '../utils/api';

/**
 * DeleteChildDialog
 * ------------------
 * Admin-grade child removal dialog. Two modes the user can pick:
 *
 *  • Archive (default, safe) — sets is_active=false but keeps every
 *    related record so OFSTED reports / incident history stay intact.
 *  • Force delete (admin-only) — cascade-deletes the child AND every
 *    record (bookings, enrollments, attendance, moments, milestones,
 *    incidents, learning reports, communications, daily logs, etc.).
 *
 * Force delete requires:
 *   • org_owner / org_admin / admin role (parents and staff cannot do it).
 *   • Typing the child's first name to confirm.
 *
 * The dialog calls `GET /api/children/{id}/dependencies` on open so the
 * admin sees exactly what's about to be wiped before they tick the box.
 */
export const DeleteChildDialog = ({
  open,
  child,
  user,
  onClose,
  onDeleted,
}) => {
  const ADMIN_ROLES = ['admin', 'org_owner', 'org_admin'];
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  const [mode, setMode] = useState('archive'); // 'archive' | 'force'
  const [deps, setDeps] = useState(null);
  const [depsLoading, setDepsLoading] = useState(false);
  const [depsError, setDepsError] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Pre-flight dependency counts when the dialog opens so the user
  // can decide informedly. We do this once per open, not per mode toggle.
  useEffect(() => {
    if (!open || !child?._id) return;
    setMode('archive');
    setConfirmName('');
    setError('');
    setDeps(null);
    setDepsError('');
    setDepsLoading(true);
    let cancelled = false;
    (async () => {
      try {
        const data = await api.getChildDependencies(child._id);
        if (!cancelled) setDeps(data);
      } catch (e) {
        if (!cancelled) setDepsError(e.message || 'Failed to load record summary.');
      } finally {
        if (!cancelled) setDepsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, child?._id]);

  if (!open || !child) return null;

  const childName = `${child.first_name || ''} ${child.last_name || ''}`.trim();
  const expectedConfirmText = (child.first_name || '').trim();

  const submit = async () => {
    setError('');
    if (mode === 'force') {
      if (!isAdmin) {
        setError('Only an admin can force-delete a child.');
        return;
      }
      if (confirmName.trim().toLowerCase() !== expectedConfirmText.toLowerCase()) {
        setError(`To confirm, type the child's first name (${expectedConfirmText}) exactly.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const res = await api.deleteChild(child._id, mode === 'force');
      if (onDeleted) onDeleted({ child, mode, response: res });
      onClose?.();
    } catch (e) {
      setError(e.message || 'Failed to delete child.');
    } finally {
      setSubmitting(false);
    }
  };

  // Build a friendly summary of what will be wiped on force-delete.
  const summary = (() => {
    if (!deps?.counts) return [];
    const labels = {
      bookings: 'bookings',
      enrollments: 'enrollment applications',
      enrollment_documents: 'enrollment documents',
      enrollment_plans: 'recurring enrollment plans',
      attendance: 'attendance records',
      dropoff_logs: 'drop-off logs',
      pickup_logs: 'pickup logs',
      pickup_codes: 'pickup codes',
      moments: 'moments / photos',
      milestones: 'milestones',
      learning_reports: 'learning reports',
      immunizations: 'immunisation records',
      incidents: 'incident reports',
      communications: 'messages',
      daily_logs: 'daily logs',
      journal_entries: 'journal entries',
      waitlist: 'waitlist entries',
      child_meal_overrides: 'meal overrides',
    };
    const rows = [];
    for (const [key, label] of Object.entries(labels)) {
      const n = deps.counts[key] || 0;
      if (n > 0) rows.push({ key, label, count: n });
    }
    return rows;
  })();

  const hasAnyRecords = summary.length > 0;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      onClick={() => !submitting && onClose?.()}
      data-testid="delete-child-dialog-overlay"
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="delete-child-dialog"
      >
        <h3 className="text-lg font-bold text-slate-900 mb-2">
          Remove {childName}
        </h3>
        <p className="text-sm text-slate-600 mb-4">
          Choose how you want to remove this child's record.
        </p>

        {/* Records summary */}
        {depsLoading && (
          <div className="text-sm text-slate-500 mb-4" data-testid="delete-child-deps-loading">
            Loading record summary…
          </div>
        )}
        {depsError && (
          <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-800" data-testid="delete-child-deps-error">
            {depsError}
          </div>
        )}
        {deps && (
          <div className="mb-4 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm" data-testid="delete-child-deps">
            <div className="font-semibold text-slate-800 mb-1">
              {hasAnyRecords ? 'Linked records found' : '✅ No linked records'}
            </div>
            {hasAnyRecords ? (
              <ul className="ml-5 list-disc text-slate-700 space-y-0.5" data-testid="delete-child-deps-list">
                {summary.map(s => (
                  <li key={s.key}>
                    <span data-testid={`dep-${s.key}`}>{s.count} {s.label}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-slate-600">
                This child has no bookings, enrollments, incidents or other history. Force delete is safe.
              </div>
            )}
          </div>
        )}

        {/* Mode picker */}
        <div className="space-y-2 mb-4">
          <label className="flex items-start gap-2 cursor-pointer p-3 rounded-lg border border-slate-200 hover:bg-slate-50">
            <input
              type="radio"
              name="delete-child-mode"
              checked={mode === 'archive'}
              onChange={() => setMode('archive')}
              className="mt-1"
              data-testid="delete-child-mode-archive"
            />
            <div>
              <div className="font-semibold text-slate-800">Archive (recommended)</div>
              <div className="text-xs text-slate-600">
                Hides {childName} from active lists but keeps every record (bookings, incidents, OFSTED moments…). Reversible by an admin.
              </div>
            </div>
          </label>
          <label
            className={`flex items-start gap-2 p-3 rounded-lg border ${isAdmin ? 'border-rose-200 hover:bg-rose-50 cursor-pointer' : 'border-slate-200 opacity-60 cursor-not-allowed'}`}
          >
            <input
              type="radio"
              name="delete-child-mode"
              checked={mode === 'force'}
              onChange={() => isAdmin && setMode('force')}
              disabled={!isAdmin}
              className="mt-1"
              data-testid="delete-child-mode-force"
            />
            <div className="flex-1">
              <div className="font-semibold text-rose-800">
                ⚠️ Force delete this child and ALL related records
              </div>
              <div className="text-xs text-slate-700">
                Permanently removes the child plus every booking, enrollment, attendance log, moment, milestone, incident, message and report linked to them. <strong>Cannot be undone.</strong>
                {!isAdmin && (
                  <div className="mt-1 text-amber-700">
                    Only an organisation admin can force-delete.
                  </div>
                )}
              </div>
            </div>
          </label>
        </div>

        {/* Confirmation when force selected */}
        {mode === 'force' && isAdmin && (
          <div className="mb-4 rounded-lg bg-rose-50 border border-rose-200 p-3" data-testid="delete-child-force-confirm-block">
            <label className="block text-sm font-semibold text-rose-900 mb-1">
              Type <span className="font-mono">{expectedConfirmText}</span> to confirm
            </label>
            <input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-rose-300 focus:outline-none focus:ring-2 focus:ring-rose-400"
              placeholder={expectedConfirmText}
              autoComplete="off"
              data-testid="delete-child-force-confirm-input"
            />
            {hasAnyRecords && (
              <p className="text-xs text-rose-800 mt-2">
                This will also permanently delete{' '}
                {summary.map((s, i) => (
                  <React.Fragment key={s.key}>
                    {i > 0 && (i === summary.length - 1 ? ' and ' : ', ')}
                    <strong>{s.count} {s.label}</strong>
                  </React.Fragment>
                ))}.
              </p>
            )}
          </div>
        )}

        {error && (
          <div className="mb-3 rounded-lg bg-rose-50 border border-rose-200 p-3 text-sm text-rose-800" data-testid="delete-child-error">
            {error}
          </div>
        )}

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={() => onClose?.()}
            disabled={submitting}
            className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="delete-child-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || depsLoading || (mode === 'force' && !isAdmin)}
            className={`px-4 py-2 rounded-lg text-white disabled:opacity-50 ${mode === 'force' ? 'bg-rose-600 hover:bg-rose-700' : 'bg-cyan-700 hover:bg-cyan-800'}`}
            data-testid="delete-child-confirm"
          >
            {submitting
              ? 'Working…'
              : mode === 'force'
                ? '⚠️ Force delete'
                : 'Archive child'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteChildDialog;
